import {
  PaymentPlanEntryStatus,
  Plan,
  PlanEntry,
} from '@debbie/common/dist/economy/payment-plans/index.js'
import { Currency, formatNumber } from '@debbie/common/dist/index.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import AlertTriangle from '@debbie/iris/dist/components/Icons/AlertTriangle.js'
import Check from '@debbie/iris/dist/components/Icons/Check.js'
import Clock from '@debbie/iris/dist/components/Icons/Clock.js'
import X from '@debbie/iris/dist/components/Icons/X.js'
import moment from 'moment'
import React from 'react'
import { useSelector } from 'react-redux'
import { styled } from 'styled-components'

import { DATE_FORMAT_SHORT } from '../theme.js'

type Props = {
  paymentPlan: Plan
  currency: Currency
}

type StatusKind = 'paid' | 'next' | 'pending' | 'overdue' | 'failed'

const PAID_STATUSES = [PaymentPlanEntryStatus.SETTLED]

const FAILED_STATUSES = [
  PaymentPlanEntryStatus.CANCELED,
  PaymentPlanEntryStatus.ERRORSETTLED,
]

const OVERDUE_STATUSES = [
  PaymentPlanEntryStatus.OVERDUE,
  PaymentPlanEntryStatus.REMINDED,
]

const isPaid = (entry: PlanEntry) => PAID_STATUSES.includes(entry.status)
const isFailed = (entry: PlanEntry) => FAILED_STATUSES.includes(entry.status)
const isOverdue = (entry: PlanEntry) => OVERDUE_STATUSES.includes(entry.status)

const getStatusKind = (entry: PlanEntry, isNext: boolean): StatusKind => {
  if (isPaid(entry)) {
    return 'paid'
  }
  if (isFailed(entry)) {
    return 'failed'
  }
  if (isOverdue(entry)) {
    return 'overdue'
  }
  if (isNext) {
    return 'next'
  }
  return 'pending'
}

const EntriesList = (props: Props) => {
  const { paymentPlan, currency } = props
  const lang = useSelector((s: RootState) => s.lang)

  const entries = paymentPlan.plan ?? []

  const paidEntries = entries.filter(isPaid)
  const totalAmount = entries.reduce((acc, e) => acc + (e.amount || 0), 0)
  const paidAmount = paidEntries.reduce((acc, e) => acc + (e.amount || 0), 0)
  const remainingAmount = Math.max(totalAmount - paidAmount, 0)
  const progressPct =
    totalAmount > 0 ? Math.min(100, (paidAmount / totalAmount) * 100) : 0
  const isComplete = entries.length > 0 && paidEntries.length === entries.length

  // First non-settled entry that is not failed = the "next" entry that the
  // debtor should focus on.
  const nextEntryIndex = entries.findIndex(
    (entry) => !isPaid(entry) && !isFailed(entry),
  )
  const nextEntry = nextEntryIndex >= 0 ? entries[nextEntryIndex] : null

  return (
    <Container>
      <ProgressCard $complete={isComplete}>
        <ProgressHeader>
          <ProgressTitle>
            {lang.debtorWebPaymentPlans.progressTitle}
          </ProgressTitle>
          <EntriesCount>
            <strong>{paidEntries.length}</strong>{' '}
            {lang.debtorWebPaymentPlans.progressOf} {entries.length}{' '}
            {lang.debtorWebPaymentPlans.paymentPlanEntries.toLowerCase()}
          </EntriesCount>
        </ProgressHeader>

        <ProgressBarOuter>
          <ProgressBarInner
            style={{ width: `${progressPct}%` }}
            $complete={isComplete}
          />
        </ProgressBarOuter>

        <ProgressMeta>
          <ProgressStat>
            <ProgressStatLabel>
              {lang.debtorWebPaymentPlans.progressPaid}
            </ProgressStatLabel>
            <ProgressStatValue $kind="paid">
              {currency} {formatNumber(paidAmount)}
            </ProgressStatValue>
          </ProgressStat>
          <ProgressStat $right>
            <ProgressStatLabel>
              {lang.debtorWebPaymentPlans.progressRemaining}
            </ProgressStatLabel>
            <ProgressStatValue>
              {currency} {formatNumber(remainingAmount)}
            </ProgressStatValue>
          </ProgressStat>
        </ProgressMeta>

        {isComplete ? (
          <CompleteBanner>
            <Check />
            <span>{lang.debtorWebPaymentPlans.progressComplete}</span>
          </CompleteBanner>
        ) : nextEntry ? (
          <NextEntryBanner>
            <NextEntryLeft>
              <NextEntryLabel>
                {lang.debtorWebPaymentPlans.progressNext}
              </NextEntryLabel>
              <NextEntryDate>
                {moment(nextEntry.date).format(DATE_FORMAT_SHORT)}
              </NextEntryDate>
            </NextEntryLeft>
            <NextEntryAmount>
              {currency} {formatNumber(nextEntry.amount || 0)}
            </NextEntryAmount>
          </NextEntryBanner>
        ) : null}
      </ProgressCard>

      <EntriesSection>
        <EntriesHeading>
          {lang.debtorWebPaymentPlans.paymentPlanEntries}
        </EntriesHeading>
        <EntriesRows>
          {entries.map((entry, index) => {
            const kind = getStatusKind(entry, index === nextEntryIndex)
            return (
              <EntryRow key={index} $kind={kind}>
                <EntryStatusIcon $kind={kind}>
                  {kind === 'paid' && <Check />}
                  {kind === 'failed' && <X />}
                  {kind === 'overdue' && <AlertTriangle />}
                  {kind === 'next' && <Clock />}
                  {kind === 'pending' && <PendingDot />}
                </EntryStatusIcon>
                <EntryDate>
                  {moment(entry.date).format(DATE_FORMAT_SHORT)}
                </EntryDate>
                <EntryAmount>
                  {currency} {formatNumber(entry.amount || 0)}
                </EntryAmount>
                <EntryStatusBadge $kind={kind}>
                  {lang.debtorWebPaymentPlans.statusCode[entry.status]}
                </EntryStatusBadge>
              </EntryRow>
            )
          })}
        </EntriesRows>
      </EntriesSection>
    </Container>
  )
}

export default EntriesList

const COLOR_PAID = '#1e7d3a'
const COLOR_PAID_BG = '#e8f5ec'
const COLOR_NEXT = '#1f6fb2'
const COLOR_NEXT_BG = '#e8f1fa'
const COLOR_PENDING = '#888'
const COLOR_PENDING_BG = '#f5f5f5'
const COLOR_OVERDUE = '#b36b00'
const COLOR_OVERDUE_BG = '#fff5e1'
const COLOR_FAILED = '#c0392b'
const COLOR_FAILED_BG = '#fdecea'

const kindColor = (kind: StatusKind): { fg: string; bg: string } => {
  switch (kind) {
    case 'paid':
      return { fg: COLOR_PAID, bg: COLOR_PAID_BG }
    case 'next':
      return { fg: COLOR_NEXT, bg: COLOR_NEXT_BG }
    case 'overdue':
      return { fg: COLOR_OVERDUE, bg: COLOR_OVERDUE_BG }
    case 'failed':
      return { fg: COLOR_FAILED, bg: COLOR_FAILED_BG }
    case 'pending':
    default:
      return { fg: COLOR_PENDING, bg: COLOR_PENDING_BG }
  }
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const ProgressCard = styled.div<{ $complete: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 20px;
  background: ${(p) => (p.$complete ? COLOR_PAID_BG : '#fafafa')};
  border: 1px solid ${(p) => (p.$complete ? COLOR_PAID : '#eee')};
  border-radius: 12px;
`

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
`

const ProgressTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111;
`

const EntriesCount = styled.span`
  font-size: 13px;
  color: #555;

  strong {
    color: #111;
    font-weight: 700;
  }
`

const ProgressBarOuter = styled.div`
  width: 100%;
  height: 10px;
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 999px;
  overflow: hidden;
`

const ProgressBarInner = styled.div<{ $complete: boolean }>`
  height: 100%;
  background: ${(p) => (p.$complete ? COLOR_PAID : COLOR_NEXT)};
  border-radius: 999px;
  transition: width 0.4s ease;
`

const ProgressMeta = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
`

const ProgressStat = styled.div<{ $right?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: ${(p) => (p.$right ? 'right' : 'left')};
`

const ProgressStatLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #888;
  font-weight: 600;
`

const ProgressStatValue = styled.span<{ $kind?: StatusKind }>`
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => (p.$kind === 'paid' ? COLOR_PAID : '#111')};
  font-variant-numeric: tabular-nums;
`

const NextEntryBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid ${COLOR_NEXT};
  border-radius: 10px;
`

const NextEntryLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const NextEntryLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${COLOR_NEXT};
  font-weight: 700;
`

const NextEntryDate = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #111;
  font-variant-numeric: tabular-nums;
`

const NextEntryAmount = styled.span`
  font-size: 17px;
  font-weight: 700;
  color: #111;
  font-variant-numeric: tabular-nums;
`

const CompleteBanner = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid ${COLOR_PAID};
  border-radius: 10px;
  color: ${COLOR_PAID};
  font-weight: 700;

  svg {
    width: 18px;
    height: 18px;
  }
`

const EntriesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const EntriesHeading = styled.h4`
  margin: 0;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #888;
  font-weight: 700;
`

const EntriesRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const EntryRow = styled.div<{ $kind: StatusKind }>`
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid ${(p) => (p.$kind === 'next' ? COLOR_NEXT : '#eee')};
  border-radius: 10px;
  background: ${(p) => (p.$kind === 'next' ? COLOR_NEXT_BG : '#fff')};

  @media (max-width: 480px) {
    grid-template-columns: auto 1fr auto;
    gap: 8px;
  }
`

const EntryStatusIcon = styled.span<{ $kind: StatusKind }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${(p) => kindColor(p.$kind).bg};
  color: ${(p) => kindColor(p.$kind).fg};
  flex-shrink: 0;

  svg {
    width: 14px;
    height: 14px;
  }
`

const PendingDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${COLOR_PENDING};
`

const EntryDate = styled.span`
  font-size: 14px;
  color: #222;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
`

const EntryAmount = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #111;
  font-variant-numeric: tabular-nums;
`

const EntryStatusBadge = styled.span<{ $kind: StatusKind }>`
  padding: 2px 8px;
  border-radius: 999px;
  background: ${(p) => kindColor(p.$kind).bg};
  color: ${(p) => kindColor(p.$kind).fg};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;

  @media (max-width: 480px) {
    display: none;
  }
`
